import { isRejected } from '@reduxjs/toolkit';
import {
    Modal,
    ModalButtonProps,
    ModalWidthEnum,
    NotificationProps,
    NotificationTypeEnum,
    useMediaSize
} from 'Components/designSystem';
import { DeclineFeedbackDialog, useOfferFeedbackDialog } from 'features/feedback';
import { Offer } from 'pages/candidateOffer/types';
import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useGoBackToOpportunities } from 'shared/hooks';
import { useErrorText } from 'shared/i18n';

import { DeclineOfferState, ListOffer } from '../types';

export type SubmitDeclineCallback = (offer: ListOffer | Offer) => Promise<unknown>;

interface DeclineOfferDialogProps {
    onCancel: () => void;
    dialogState: DeclineOfferState | null;
    onConfirm: SubmitDeclineCallback;
}

function DeclineOfferDialog(props: DeclineOfferDialogProps) {
    const { onCancel, onConfirm, dialogState } = props;

    const { status, offer, error } = dialogState ?? {};

    const goBack = useGoBackToOpportunities();

    const { isMobile } = useMediaSize();

    const { t } = useTranslation('candidateOffers');
    const errorText = useErrorText();

    const submitting = status === 'pending';
    
    const { onOpen: openFeedbackDeclineOfferDialog, ...declineOfferDialogState } = useOfferFeedbackDialog('OfferDeclineFeedback', {
        onClose: goBack
    });

    const handleConfirm = useCallback(async () => {
        if (!offer) {
            return;
        }

        const result = await onConfirm(offer);

        // A rejected thunk still fulfils its promise, so the outcome has to be read from the
        // action. Without this the candidate is walked through the whole "you have declined"
        // flow while the offer is untouched.
        if (isRejected(result)) {
            return;
        }

        const shouldShowFeedbackDialog = localStorage.getItem('doNotShowDeclineFeedback') !== 'true';

        if (!shouldShowFeedbackDialog) {
            goBack?.();

            return;
        }

        const orderId = 'orderId' in offer ? offer.orderId : offer.id;

        openFeedbackDeclineOfferDialog(
            orderId,
            offer.orderRoleId || ''
        );
    }, [ onConfirm, offer, goBack, openFeedbackDeclineOfferDialog ]);

    const handleClose = useCallback(() => {
        onCancel();
    }, [ onCancel ]);

    const submitButton = useMemo<ModalButtonProps>(() => ({
        action: handleConfirm,
        inProgress: submitting,
        title: t('dialog.decline.submit')
    }), [ submitting, handleConfirm, t ]);

    const cancelButton = useMemo<ModalButtonProps>(() => ({
        action: handleClose,
        disabled: submitting,
        title: t('dialog.decline.cancel')
    }), [ handleClose, submitting, t ]);

    const errorNotification = useMemo<NotificationProps | undefined>(() => {
        if (!error) {
            return undefined;
        }

        return {
            type: NotificationTypeEnum.Error,
            text: errorText(error)
        };
    }, [ error, errorText ]);

    return (
        <>
            <Modal
                title={ t('declineDialogTitle') }
                buttonAction={ submitButton }
                buttonCancel={ cancelButton }
                notification={ errorNotification }
                width={ isMobile ? ModalWidthEnum.FULL : ModalWidthEnum.XS }
                onOverlayClick={ handleClose }
                open={ Boolean(offer) }
            >
                { t('declineDialogSubtitle') }
            </Modal>
                    
            <DeclineFeedbackDialog { ...declineOfferDialogState } />
        </>
    );
}

export default memo(DeclineOfferDialog);
